import axios from 'axios';
import { BrowserClient, Hub, Severity } from '@sentry/browser';

const httpClient = axios.create({
  baseURL: `${process.env.VUE_APP_API_URL}/api`,
  timeout: 1000 * 60 * 5, // 5 minutes
  withCredentials: true
});

const log = new BrowserClient({
  dsn: process.env.VUE_APP_SENTRY_URL,
  environment: process.env.NODE_ENV
});

httpClient.interceptors.request.use(
  (config) => {
    return Promise.resolve(config);
  },
  (err) => {
    return Promise.reject(err);
  }
);

// Pulls the human-readable message the API sent back. Web API returns different shapes depending on how
// the failure was raised: a thrown exception (exceptionMessage), a BadRequest with a string/message body,
// or ModelState validation errors.
function serverMessage(response) {
  const data = response?.data;
  if (!data) return null;
  if (typeof data === 'string') return data.trim() || null;

  const direct = data.exceptionMessage || data.message || data.Message || data.error;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();

  const modelState = data.modelState || data.ModelState;
  if (modelState) {
    const messages = Object.values(modelState).flat().filter(Boolean);
    if (messages.length) return messages.join(' ');
  }

  return null;
}

httpClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const fromServer = serverMessage(error.response);

    if (fromServer) {
      // The API said something useful — show that, not "Request failed with status code 400".
      error.message = fromServer;
    } else if (error.config) {
      error.message = `${error.config.url}: ${error.message}`;
    }

    const hub = new Hub(log);

    const user = JSON.parse(localStorage.getItem('user')) || {};

    hub.addBreadcrumb({
      category: 'user',
      message: JSON.stringify({
        email: user.email
      }),
      level: Severity.Info
    });

    const config = error.config || {};
    hub.addBreadcrumb({
      category: 'xhr',
      message: `${config.method} ${config.url}: ${JSON.stringify((error.response || {}).data)}`,
      level: Severity.Info
    });

    hub.captureException(error);
    return Promise.reject(error);
  }
);

export default httpClient;
